import React, {useEffect, useState} from 'react';
import './Sidebar.css';
import { useTabs } from '../../contexts/TabContext';
import { useDomain, DOMAINS } from '../../contexts/DomainContext';
import { Box, Typography, useTheme, FormControl, Select, MenuItem } from '@mui/material';
import useLocalStorageVO from "../Common/UseLocalStorageVO";
import useSystemStatusManager from "../../hook/UseSystemStatusManager";
import { getCompCdWithNameBySite, setAffiliation } from '../../api/rest/authApi';

const Sidebar = ({ items, expandedItems = ['main'], onItemClick, onToggleItem }) => {
  const { activeTab } = useTabs();
  const { domain, domainName } = useDomain();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const { loginUser, isBranchManagerLoginUser, setUserInfo: setUserInfoInStorage } = useLocalStorageVO();
  const {compCdGroup = compCdGroup, siteGroup =siteGroup } = useSystemStatusManager();
  const [userInfo, setUserInfo] = useState(null);
  
  // 지부장 관련 상태
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompCd, setSelectedCompCd] = useState(loginUser.compCd || '');

  // expandedItems props가 없을 경우 기본값으로 ['main'] 사용
  const handleToggle = (id) => {
    if (onToggleItem) {
      onToggleItem(id);
    }
  };

  const handleClick = (id) => {
    if (onItemClick) {
      onItemClick(id);
    }
  };

  // 도메인에 따라 로고 클래스 반환
  const getLogoClass = () => {
    return domain === DOMAINS.PEMS ? 'pems-logo system-logo' : 'imos-logo system-logo';
  };

  // 도메인별 색상 설정
  const getBgColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(45, 30, 15, 0.5)' : 'rgba(252, 235, 212, 0.6)';
    }
    return isDarkMode ? 'rgba(16, 42, 67, 0.5)' : 'rgba(232, 240, 253, 0.6)';
  };
  
  const getBorderColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#3d2814' : '#f5e8d7';
    }
    return isDarkMode ? '#1a365d' : '#dae5f5';
  };

  const getTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.7)';
    }
    return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.7)';
  };

  const companyName = compCdGroup.find(c=>c.compCd === loginUser.compCd)?.companyName;
  const siteName = siteGroup.find(s=>s.codeId === loginUser.site)?.codeName

  useEffect(() => {
    if (companyName !== null && companyName !== undefined) {
      const siteInfo = (siteName !== null && siteName !== undefined) ? siteName+' ' : ''
      setUserInfo(siteInfo);
    }
  }, [siteName, companyName ]);

  // 지부장 로그인 사용자의 경우 회사 목록 로드
  useEffect(() => {
    const loadCompanies = async () => {
      if (loginUser.isBranchManagerLogin && loginUser.loginId) {
        try {
          const companies = await getCompCdWithNameBySite(loginUser.loginId);
          setAvailableCompanies(Array.isArray(companies) ? companies : []);
        } catch (error) {
          console.error('Failed to load companies for branch manager:', error);
          setAvailableCompanies([]);
        }
      }
    };

    loadCompanies();
  }, [loginUser.loginId, loginUser.isBranchManagerLogin]);

  // 회사 변경 핸들러
  const handleCompanyChange = async (newCompCd) => {
    console.log('Company change requested:', { newCompCd, currentCompCd: loginUser.compCd });
    
    // 같은 회사를 선택한 경우 아무것도 하지 않음
    if (newCompCd === loginUser.compCd) {
      console.log('Same company selected, no change needed');
      return;
    }
    
    try {
      setSelectedCompCd(newCompCd);
      // setAffiliation API 호출하여 소속 변경
      const result = await setAffiliation(newCompCd);

      if (result.status === 200) {
        // 사용자 정보 업데이트 (지부장 로그인 플래그 유지)
        setUserInfoInStorage(result, true);
        
        // 페이지 새로고침으로 변경사항 반영
        window.location.reload();
      } else {
        console.error('Failed to change affiliation:', result.message);
        // 실패 시 이전 값으로 되돌림
        setSelectedCompCd(loginUser.compCd || '');
      }
    } catch (error) {
      console.error('Error changing company:', error);
      // 에러 시 이전 값으로 되돌림
      setSelectedCompCd(loginUser.compCd || '');
    }
  };

  // const allowedUsers = ['안계홍', '테스터']; // 허용된 사용자 목록

  return (
    <div className="sidebar">
      <div className="sidebar-title">
        <h2 className={getLogoClass()}>{domainName}</h2>
          <Typography variant="subtitle2">{userInfo}</Typography>
        
        {/* 지부장 로그인 사용자용 회사 선택 박스 */}
        {isBranchManagerLoginUser() && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedCompCd}
                onChange={(e) => handleCompanyChange(e.target.value)}
                displayEmpty
                variant="outlined"
                sx={{
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '0.875rem',
                    color: getTextColor(),
                    textAlign: 'center',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: getBorderColor(),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: getBorderColor(),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: domain === DOMAINS.PEMS ? '#d35400' : '#1976d2',
                  },
                  backgroundColor: getBgColor(),
                }}
              >
                {availableCompanies.map((company) => (
                  <MenuItem key={company.compCd} value={company.compCd}>
                    {company.companyName || company.compCd}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </div>
      <nav className="sidebar-nav">
        <ul className="sidebar-menu">
          {items.map(item => {
            // "디지털 트윈" 메뉴이고, 로그인한 사용자 이름이 허용 목록에 없으면 렌더링하지 않음
            // if (item.id === 'digital-twin' && !allowedUsers.includes(loginUser?.userNm)) {
            //   return null; // 허용된 목록에 포함되지 않으면 메뉴 숨김
            // }

            return (
              <li key={item.id} className={item.isActive ? 'active' : ''}>
                <div
                  className="menu-item"
                  onClick={() => {
                    // 확장/축소 토글
                    handleToggle(item.id);

                    // 서브메뉴가 없는 항목(예: 메인 메뉴)은 클릭 시 해당 페이지로 이동
                    if (!item.subItems || item.subItems.length === 0) {
                      handleClick(item.id);
                    }
                  }}
                >
                  <div className="menu-title">
                    <span className="icon">{item.icon}</span>
                    <span className="menu-name">{item.name}</span>
                  </div>
                  {item.subItems && item.subItems.length > 0 && (
                    <span className="expand-icon">
                      {expandedItems.includes(item.id) ? '▼' : '▶'}
                    </span>
                  )}
                </div>

                {item.subItems && expandedItems.includes(item.id) && (
                  <ul className="submenu">
                    {item.subItems.map(subItem => {
                      // 현재 활성화된 탭과 비교
                      const isActive = subItem.id === activeTab;
                      return (
                        <li key={subItem.id} className={isActive ? 'active' : ''}>
                          <a
                            href={`#${subItem.id}`}
                            className={isActive ? 'active' : ''}
                            onClick={(e) => {
                              e.preventDefault();
                              handleClick(subItem.id);
                            }}
                          >
                            <span>{subItem.name}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
      
      {/* 저작권 정보 추가 */}
      <Box className="sidebar-footer" sx={{ 
        mt: 'auto', 
        p: 1, 
        bgcolor: getBgColor(), 
        borderTop: `1px solid ${getBorderColor()}`,
        position: 'sticky',
        bottom: 0
      }}>
        <Typography variant="caption" sx={{ 
          textAlign: 'center', 
          display: 'block', 
          opacity: 0.7, 
          whiteSpace: 'pre-line',
          fontSize: '0.7rem',
          color: getTextColor()
        }}>
          © 2025 {domainName} 시스템.{'\n'}All rights reserved.
        </Typography>
      </Box>
    </div>
  );
};

export default Sidebar;